import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { ConnectWalletButton } from '../auth/ConnectWalletButton';
import { BrandLogo } from '../brand/BrandLogo';
import { LineOfficialLink } from './LineOfficialLink';
import { MobileNavDrawer } from './MobileNavDrawer';
import {
  ChevronDown,
  navItems,
  isNavActive,
  isNavGroupActive,
} from './navItems';

function DesktopNavLink({ href, label, icon: Icon, active }) {
  return (
    <Link
      to={href}
      className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors lg:px-4 ${
        active
          ? 'bg-brand-red text-white'
          : 'text-gray-700 hover:bg-red-50 hover:text-brand-red'
      }`}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </Link>
  );
}

function DesktopNavGroup({ item, pathname }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const groupActive = isNavGroupActive(pathname, item.children);
  const Icon = item.icon;

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors lg:px-4 ${
          groupActive || open
            ? 'bg-brand-red text-white'
            : 'text-gray-700 hover:bg-red-50 hover:text-brand-red'
        }`}
      >
        <Icon className="h-4 w-4" />
        <span>{item.label}</span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute left-0 top-full z-50 mt-1 min-w-[200px] rounded-xl border border-red-100 bg-white p-1 shadow-lg"
        >
          {item.children.map(({ href, label, icon: ChildIcon, badge }) => {
            const active = isNavActive(pathname, href);
            return (
              <Link
                key={href}
                to={href}
                role="menuitem"
                onClick={() => setOpen(false)}
                className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
                  active
                    ? 'bg-brand-red text-white'
                    : 'text-gray-700 hover:bg-red-50 hover:text-brand-red'
                }`}
              >
                <ChildIcon className="h-4 w-4" />
                <span className="flex-1">{label}</span>
                {badge ? (
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                      active
                        ? 'bg-brand-gold text-red-950'
                        : 'bg-brand-gold/20 text-brand-gold'
                    }`}
                  >
                    {badge}
                  </span>
                ) : null}
              </Link>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export function Header() {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setIsMenuOpen(false), []);

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-red-100 bg-white shadow-sm">
        <div className="mx-auto flex items-center justify-between gap-3 px-4 py-3 md:py-4">
          <Link to="/" className="flex min-w-0 items-center gap-2 md:gap-3">
            <BrandLogo size="sm" />
            <div className="min-w-0">
              <p className="hidden text-xs font-medium uppercase tracking-widest text-brand-gold sm:block">
                Mungkorn
              </p>
              <h1 className="truncate text-base font-bold text-brand-red md:text-xl">
                MUNGKORN LOTTO
              </h1>
            </div>
          </Link>

          <nav className="hidden items-center gap-2 md:flex lg:gap-4">
            {navItems.map((item) =>
              item.children ? (
                <DesktopNavGroup
                  key={item.id}
                  item={item}
                  pathname={location.pathname}
                />
              ) : (
                <DesktopNavLink
                  key={item.href}
                  href={item.href}
                  label={item.label}
                  icon={item.icon}
                  active={isNavActive(location.pathname, item.href)}
                />
              ),
            )}
            <LineOfficialLink />
            <ConnectWalletButton />
          </nav>

          <button
            type="button"
            onClick={() => setIsMenuOpen(true)}
            aria-label="เปิดเมนู"
            aria-expanded={isMenuOpen}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-brand-red transition-colors hover:bg-red-50 md:hidden"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </header>

      <MobileNavDrawer isOpen={isMenuOpen} onClose={closeMenu} />
    </>
  );
}
